import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import FixModal from "@/components/FixModal";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { FileCode2, FileText, ScissorsSquare, RefreshCw, AlignLeft, Wand2 } from "lucide-react";

const ICONS = {
  missing_faq_schema: FileCode2,
  no_llms_txt: FileText,
  not_answer_first: AlignLeft,
  section_too_long: ScissorsSquare,
  stale_content: RefreshCw,
};

const TYPE_LABEL = {
  missing_faq_schema: "FAQ schema",
  no_llms_txt: "llms.txt",
  not_answer_first: "Answer-first",
  section_too_long: "Too long",
  stale_content: "Stale",
};

export default function Artefacts() {
  const { current, projects, selectProject } = useProject();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, s] = await Promise.all([
          api.get("/artefacts"),
          api.get("/artefacts/stats/weekly"),
        ]);
        setItems(a.data);
        setStats(s.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Archive</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Generated artefacts
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Every "Fix this" click ships a copy-paste artefact here. Revisit
          schema blocks, rewrites, split plans and deploy guides at any time.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat label="This week" value={stats.generated_count} helper="artefacts generated" />
          <Stat label="Completed" value={stats.completed_count} helper="actions shipped" />
          <Stat
            label="Schema"
            value={stats.by_type?.missing_faq_schema || 0}
            helper="FAQ JSON-LD"
          />
          <Stat
            label="Rewrites"
            value={stats.by_type?.not_answer_first || 0}
            helper="Answer-first"
          />
        </div>
      )}

      <section className="card-flat" data-testid="artefacts-list">
        <div className="bg-[#fafafa] border-b border-zinc-200 px-5 py-3 grid grid-cols-12 gap-4 label-eyebrow">
          <div className="col-span-3">When</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-6">Title</div>
          <div className="col-span-1 text-right">#</div>
        </div>
        <div className="divide-y divide-zinc-200">
          {loading && (
            <div className="p-8 text-center text-sm text-zinc-500">
              Loading…
            </div>
          )}
          {!loading &&
            items.map((it) => {
              const Icon = ICONS[it.type] || Wand2;
              return (
                <button
                  key={it.artefact_id}
                  onClick={() => setSelected(it)}
                  data-testid={`artefact-row-${it.artefact_id}`}
                  className="w-full text-left px-5 py-4 grid grid-cols-12 gap-4 items-center text-sm hover:bg-[#fafafa] transition-colors"
                >
                  <div className="col-span-3 mono text-xs text-zinc-500">
                    {new Date(it.created_at).toLocaleString()}
                  </div>
                  <div className="col-span-2">
                    <span className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-[0.14em] text-zinc-600">
                      <Icon size={12} /> {TYPE_LABEL[it.type] || it.type}
                    </span>
                  </div>
                  <div className="col-span-6 text-zinc-800 truncate">
                    {it.title}
                  </div>
                  <div className="col-span-1 text-right text-zinc-400">→</div>
                </button>
              );
            })}
          {!loading && !items.length && (
            <div className="p-12 text-center text-sm text-zinc-500">
              Nothing yet. Run an audit on the Content Optimizer and click
              "Fix this" on any finding.
            </div>
          )}
        </div>
      </section>

      <FixModal artefact={selected} onClose={() => setSelected(null)} />
    </DashboardShell>
  );
}

function Stat({ label, value, helper }) {
  return (
    <div className="card-flat p-4">
      <div className="label-eyebrow">{label}</div>
      <div className="display text-3xl font-black mt-2 tabular-nums">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-zinc-500">{helper}</div>
    </div>
  );
}
